import DOMPurify from 'dompurify';

// Allowlist for HTML written by the Quill 1.3.7 editors (notes, recipe
// execution, progress descriptions).
const ALLOWED_TAGS = [
  'p', 'br', 'div', 'span',
  'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ol', 'ul', 'li',
  'blockquote', 'pre', 'code',
  'a', 'img',
];

const ALLOWED_ATTR = ['href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'class', 'style', 'data-checked'];

const COLOR = /^(#[0-9a-f]{3,8}|rgba?\(\s*[\d.]+%?\s*(,\s*[\d.]+%?\s*){2,3}\)|[a-z]+)$/i;
const ALLOWED_STYLES = {
  color: COLOR,
  'background-color': COLOR,
  'text-align': /^(left|right|center|justify)$/i,
};

const purifier = DOMPurify(window);

purifier.addHook('uponSanitizeAttribute', (node, data) => {
  if (data.attrName === 'class') {
    data.attrValue = data.attrValue.split(/\s+/).filter(c => /^ql-[a-z0-9-]+$/i.test(c)).join(' ');
  } else if (data.attrName === 'style') {
    data.attrValue = data.attrValue.split(';')
      .map(decl => decl.split(':').map(part => part.trim()))
      .filter(([prop, value, ...rest]) => !rest.length && ALLOWED_STYLES[prop?.toLowerCase()]?.test(value))
      .map(([prop, value]) => `${prop.toLowerCase()}: ${value};`)
      .join(' ');
  } else if (data.attrName === 'href') {
    // Stored notes can carry stray JSON escapes around links (`\https://…\`); no real URL has them.
    data.attrValue = data.attrValue.replace(/^\\+|\\+$/g, '');
  }
  if (data.attrValue === '') data.keepAttr = false;
});

purifier.addHook('afterSanitizeAttributes', node => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

export function sanitizeHtml(html) {
  if (typeof html !== 'string' || !html) return '';
  return purifier.sanitize(html, CONFIG);
}

// For user text interpolated into markup that renders as HTML, such as `html: true` dialog bodies.
export function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
